import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import ActionButton from "./formInputs/buttons/ActionButton";
import BreadCrumb from "./BreadCrumb";
import EprDao from "../dao/EprDao";
import Header from "./header/Header";
import LoadingSvg2 from "./LoadingSvg2";
import RbA from "./RbA";
import EprStudentInfoBlock from "./studentInfoBlock/EprStudentInfoBlock";
import StudentInfoDao from "../dao/StudentInfoDao";
import T from "./translations/Translations";

import { GlobalContext } from "./contextProvider/ContextProvider";

import "../styles/EprLinks.scss";

const EprLinks = () => {
    const { studentNumber } = useParams();

    const { dispatch, state } = useContext(GlobalContext);
    const { gradingProfiles, schoolYearDto, studentsIc, token } = state || {};

    const [hgps, setHgps] = useState(null);
    const [loader, setLoader] = useState(false);
    const [resultMessages, setResultMessages] = useState([]);
    const [selectedStudent, setSelectedStudent] = useState(null);

    const allowedRolesArray = ["EXTERNAL_GUARDIAN", "GUARDIAN"];
    const navigate = useNavigate();
    const failure1 = T({ key: "EPR_Failure1" });
    const failure2 = T({ key: "EPR_Failure2" });
    const noData = T({ key: "EPR_NoData" });
    const success = T({ key: "EPR_Success" });
    const title = T({ key: "EPR_Title" });

    // helper to convert blob to text
    const blobToText = async (blob) => {
        return await new Response(blob).text();
    };

    const downloadPdf = async (key, schoolYearName) => {
        try {
            const options = {
                action: "eprPdfDownload",
                gradingProfileId: key,
                studentNumber,
                token
            };
            setLoader(true);
            const response = await EprDao(options);
            if (response) {
                const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
                const link = document.createElement("a");
                link.href = url;
                link.setAttribute(
                    "download",
                    `epr_${selectedStudent?.lastName}_${selectedStudent?.firstName}_${studentNumber}.pdf`
                );
                document.body.appendChild(link);
                link.click();
                link.parentNode.removeChild(link);
                window.URL.revokeObjectURL(url);
                setResultMessages((prevState) => {
                    return [
                        {
                            error: false,
                            message: `${schoolYearName} ${success}.`
                        },
                        ...(prevState || [])
                    ];
                });
            }
        } catch (error) {
            if (error.response && error.response.data instanceof Blob) {
                const text = await blobToText(error.response.data);

                setResultMessages((prevState) => {
                    return [
                        { error: true, message: `${failure1} ${schoolYearName}. ${failure2}:\n${text}` },
                        ...(prevState || [])
                    ];
                });
            }
        } finally {
            setLoader(false);
        }
    };

    useEffect(() => {
        if (gradingProfiles && gradingProfiles[studentNumber] && !hgps) {
            const hgpList = Object.values(gradingProfiles[studentNumber]);
            setHgps(hgpList);
        }
    }, [hgps, gradingProfiles, studentNumber]);

    useEffect(() => {
        if (hgps || !schoolYearDto || !studentNumber || !token) {
            setLoader(false);
            return;
        }

        /**
         * @name fetchEprArchive
         * @description Check if the student has an EPR archive
         * @param {{}} app
         * @returns {{}} updatedApp
         */
        const fetchEprArchive = async () => {
            try {
                const eprOptions = {
                    action: "archiveGradingProfilesRead",
                    schoolYearKey: schoolYearDto.key,
                    studentNumber,
                    token
                };
                setLoader(true);
                const response = await EprDao(eprOptions);
                if (response && response?.data) {
                    const { payload } = response.data;
                    payload?.sort((a, b) => {
                        return parseInt(a.key, 10) < parseInt(b.key, 10) ? 1 : -1;
                    });
                    if (payload && payload.length > 0) {
                        dispatch({
                            type: "GradingProfiles",
                            gradingProfiles: {
                                ...gradingProfiles,
                                [studentNumber]: [...payload]
                            }
                        });
                    }
                }

                return true;
            } catch (error) {
                console.error("Unable to retrieve EPR Archive:\n", error);
            } finally {
                setLoader(false);
            }
        };

        if ((gradingProfiles && !gradingProfiles[studentNumber]) || !gradingProfiles) {
            fetchEprArchive();
        }
    }, [dispatch, gradingProfiles, hgps, schoolYearDto, studentNumber, token]);

    /**
     * GETTING STUDENT INFO DTO
     * Grab the StudentInfoDto using studentNumber
     */
    useEffect(() => {
        if (selectedStudent || !studentNumber || !token) {
            return;
        }
        let studentMatch = null;
        if (studentsIc) {
            studentMatch = studentsIc.find((obj) => parseInt(obj.studentNumber, 10) === parseInt(studentNumber, 10));
            if (studentMatch) {
                setSelectedStudent(studentMatch);
            }

            return;
        }

        const fetchStudentInfo = async () => {
            try {
                // get the student's info
                const options = {
                    action: "studentInfoRead",
                    params: {
                        checkForActiveEnrollment: true
                    },
                    studentNumber,
                    token
                };
                setLoader(true);
                const response = await StudentInfoDao(options);
                if (response && response.data) {
                    const { payload } = response.data;
                    dispatch({
                        type: "StudentsIc",
                        studentsIc: [payload]
                    });
                    setSelectedStudent(payload);
                }
            } catch (error) {
                console.error("Unable to retrieve student info:\n", error);
            } finally {
                setLoader(false);
            }
        };
        if (!selectedStudent) {
            fetchStudentInfo();
        }
    }, [dispatch, selectedStudent, studentNumber, studentsIc, token]);

    return (
        <RbA allowedRoles={allowedRolesArray} redirect="/notFound">
            <div style={{ display: "flex", flexDirection: "column" }}>
                <Header />
                {/* DCSD Guardian Dashboard */}
                <BreadCrumb label={T({ key: "Breadcrumb_EPR" })} />
                <div className="gutter-90">
                    <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "10px" }}>
                        {studentNumber && selectedStudent && <EprStudentInfoBlock studentInfo={selectedStudent} />}
                        <div className="back-to-dashboard">
                            <ActionButton
                                className="action-button-reg"
                                label={T({ key: "Global_BackToDashboard" })}
                                onClick={() => {
                                    navigate("/home");
                                }}
                            />
                        </div>
                    </div>
                    <h2>{title}</h2>
                    <hr />
                    <div className="display-container">
                        <div className="epr-links-container">
                            {hgps &&
                                hgps.map((obj) => {
                                    const { gradeName, key, schoolYearName, teacherName } = obj;
                                    let grade = gradeName;
                                    if (
                                        ["K", "Kindergarten", "K_FULL", "K_HALF", "K_HALF_AM", "K_HALF_PM"].includes(
                                            gradeName
                                        )
                                    ) {
                                        grade = "Kindergarten";
                                    } else {
                                        grade = `${gradeName} Grade`;
                                    }

                                    return (
                                        <ActionButton
                                            className="action-button-reg epr-link"
                                            key={obj.key}
                                            label={`${schoolYearName} -- ${grade} -- ${teacherName}`}
                                            onClick={() => {
                                                downloadPdf(key, schoolYearName);
                                            }}
                                        />
                                    );
                                })}
                            {!loader && !hgps && <p>{noData}</p>}
                        </div>
                        <div className="results-container">
                            {resultMessages &&
                                resultMessages.map((message, index) => {
                                    return (
                                        <p key={index} className={message.error ? "error" : "success"}>
                                            {message.message}
                                        </p>
                                    );
                                })}
                        </div>
                    </div>
                </div>
                {loader && <LoadingSvg2 />}
            </div>
        </RbA>
    );
};

export default EprLinks;
